import { useEffect, useState } from 'react';
import axios from 'axios';
import { CONFIG } from 'variables/config';
import { toast } from 'react-toastify';
import { authConfig, fetchPurchaseOrderVolumes, marketFor, plural, PurchaseOrderVolume, TemplateOverview } from 'services/integrations';
import { PriceOrigin } from './pricing';

/**
 * Minimum price per activity of one site form — the "Prix" tab and the "Vos prix" step of
 * Installation (same component, nothing duplicated). One row per activity found on the form's
 * pages: its price and where it comes from (the account price from Mes prix, proposed by default,
 * or this form's own), the demand, and how many pages sell it (→ Pages tab, filtered).
 * Saving the account price itself just follows it again (nothing is copied).
 */
const ActivityPricesPanel = ({
    templateId,
    overview,
    refreshOverview,
    onTemplateChange,
    user,
    onShowPages,
}: {
    templateId: string;
    overview: TemplateOverview | null;
    refreshOverview: () => void;
    onTemplateChange: (t: any) => void;
    user: any;
    onShowPages: (activity: { industry: string; subIndustry: string }) => void;
}) => {
    const [volumes, setVolumes] = useState<PurchaseOrderVolume[]>([]);
    const [drafts, setDrafts] = useState<Record<string, string>>({});
    const [saving, setSaving] = useState<string | null>(null);

    useEffect(() => {
        fetchPurchaseOrderVolumes()
            .then(setVolumes)
            .catch(() => {});
    }, []);

    const activities = overview?.activities || [];

    const save = async (industry: string, subIndustry: string, value: number | null, accountPrice: number | null) => {
        const key = `${industry}|${subIndustry}`;
        const followAccount = value == null || value === accountPrice;
        setSaving(key);
        try {
            const res = await axios.patch(
                `${CONFIG.apiUrl}/form-template/${templateId}/activity-price`,
                { industry, subIndustry: subIndustry || undefined, minimumPrice: followAccount ? null : value },
                authConfig()
            );
            onTemplateChange(res.data);
            setDrafts((d) => {
                const next = { ...d };
                delete next[key];
                return next;
            });
            refreshOverview();
            toast.success(followAccount ? 'Cette activité suit le prix du compte' : 'Prix enregistré pour ce formulaire');
        } catch (e: any) {
            toast.error(e?.response?.data?.status || "Erreur lors de l'enregistrement du prix");
        } finally {
            setSaving(null);
        }
    };

    if (!overview) return <div className="py-10 text-center font-hanken text-[14px] text-[#8A8172]">Chargement…</div>;

    if (activities.length === 0) {
        return (
            <div className="rounded-[16px] border border-dashed border-[#DDD5C9] bg-[#FAF8F4] p-7 text-center font-hanken text-[14px] text-[#6B6357]">
                <strong className="text-[#1A1613]">Pas encore d'activité.</strong>
                <br />
                Les activités apparaissent dès que les pages de votre site sont connues : installez le formulaire, ou ajoutez vos pages
                dans l'onglet Pages.
            </div>
        );
    }

    return (
        <div className="overflow-hidden rounded-[18px] border border-[#EDE7DE] bg-white font-hanken">
            {activities.map((a) => {
                const key = `${a.industry}|${a.subIndustry}`;
                const draft = drafts[key] ?? (a.minimumPrice != null ? String(a.minimumPrice) : '');
                const changed = draft !== (a.minimumPrice != null ? String(a.minimumPrice) : '');
                const market = marketFor(volumes, a.subIndustry, user);
                const submit = () => {
                    const value = parseFloat(draft.replace(',', '.'));
                    if (!draft || isNaN(value) || value <= 0) {
                        toast.error('Indiquez un prix minimum valide');
                        return;
                    }
                    save(a.industry, a.subIndustry, value, a.accountPrice);
                };
                return (
                    <div key={key} className={`flex flex-wrap items-start justify-between gap-3 border-b border-[#F1ECE4] px-5 py-4 last:border-0 ${a.minimumPrice == null ? 'bg-[#FCF9EF]' : ''}`}>
                        <div className="min-w-[200px] flex-1">
                            <strong className="block text-[15px] text-[#14110E]">{a.subIndustry || a.industry}</strong>
                            <span className="mt-0.5 block text-[12.5px] text-[#6B6357]">
                                {market.orders > 0
                                    ? `${plural(market.orders, "ordre d'achat actif", "ordres d'achat actifs")}${market.sellerPrice != null ? ` · à partir de ${market.sellerPrice} € pour vous` : ''}`
                                    : "Pas d'ordre d'achat actif pour le moment"}
                            </span>
                            {a.pages > 0 && (
                                <button onClick={() => onShowPages({ industry: a.industry, subIndustry: a.subIndustry })} className="mt-1 text-[12.5px] font-semibold text-[#241C5C] hover:underline">
                                    {plural(a.pages, 'page', 'pages')}
                                    {a.pages - a.verifiedPages > 0 ? ` · ${a.pages - a.verifiedPages} à vérifier` : ''} →
                                </button>
                            )}
                        </div>
                        <div className="flex flex-col items-end gap-1.5">
                            <span className="flex flex-wrap items-center justify-end gap-2">
                                <span className="flex w-[104px] items-center rounded-[10px] border border-[#DDD5C9] bg-white">
                                    <input
                                        value={draft}
                                        placeholder="—"
                                        inputMode="decimal"
                                        aria-label={`Prix minimum par lead, ${a.subIndustry || a.industry}`}
                                        onChange={(e) => setDrafts((d) => ({ ...d, [key]: e.target.value }))}
                                        onKeyDown={(e) => e.key === 'Enter' && submit()}
                                        className="w-full min-w-0 bg-transparent px-2.5 py-2 text-[14px] font-semibold outline-none"
                                    />
                                    <span className="pr-2.5 text-[#9A9184]">€</span>
                                </span>
                                {changed && (
                                    <button
                                        onClick={submit}
                                        disabled={saving === key}
                                        className="rounded-[9px] bg-[#241C5C] px-3 py-[7px] text-[12.5px] font-semibold text-white hover:bg-[#332a75] disabled:opacity-50"
                                    >
                                        {saving === key ? 'Enregistrement…' : 'Enregistrer'}
                                    </button>
                                )}
                                {!changed && (
                                    <span
                                        className={`rounded-full px-2.5 py-[3px] text-[12px] font-semibold ${
                                            a.minimumPrice != null ? 'bg-[#D9F0DE] text-[#146334]' : 'bg-[#FCF2D4] text-[#7A5A05]'
                                        }`}
                                    >
                                        {a.minimumPrice != null ? 'Prix fixé' : 'Prix à fixer'}
                                    </span>
                                )}
                            </span>
                            <PriceOrigin
                                source={a.priceSource}
                                accountPrice={a.accountPrice}
                                onUseAccountPrice={() => save(a.industry, a.subIndustry, null, a.accountPrice)}
                            />
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default ActivityPricesPanel;
